import { ApiClient } from "@ucl/library";
import variables from "@config/variables";

/**
 * Factory function to create an authenticated API client
 * The client will automatically include the bearer token from the provided token getter function
 * 
 * @param tokenGetter - Function that returns the current access token
 * @returns An ApiClient instance with authentication interceptor
 */
export const createAuthenticatedApiClient = (
  tokenGetter: () => string | null
): ApiClient => {
  // Create an interceptor that automatically adds the bearer token
  const authInterceptor = async (config: any) => {
    const token = tokenGetter();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };

  // Create and return the authenticated API client
  return new ApiClient(variables.apiBaseUrl, authInterceptor);
};

/**
 * Factory function to create an authenticated API client with error handlers
 * 
 * @param tokenGetter - Function that returns the current access token
 * @param errorHandlers - Optional error handlers for specific HTTP status codes
 * @returns An ApiClient instance with authentication interceptor and error handlers
 */
export const createAuthenticatedApiClientWithErrorHandlers = (
  tokenGetter: () => string | null,
  errorHandlers?: {
    onForbidden?: () => void;
    onUnauthorized?: () => void;
  }
): ApiClient => {
  // Create an interceptor that automatically adds the bearer token
  const authInterceptor = async (config: any) => {
    const token = tokenGetter();
    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  };

  // Create and return the authenticated API client
  return new ApiClient(
    variables.apiBaseUrl, 
    authInterceptor, 
    errorHandlers?.onForbidden ? { onForbidden: errorHandlers.onForbidden } : undefined
  );
}; 